import { useEffect, useMemo, useRef } from "react";
import { Timer, AlertTriangle } from "lucide-react";
import {
  deriveMatchClock,
  getMatchClockSettings,
  type MatchClockState,
} from "@/lib/matchClock";
import type { MatchSnapshot } from "@/lib/matches";
import { trackEvent } from "@/lib/audit";
import { useMounted } from "@/hooks/useMounted";

interface Props {
  minute: number;
  status?: MatchSnapshot["status"] | "PRE" | "ET" | "PEN" | "SUSP";
  /** Sub-minute seconds (0..59) for live MM:SS display. Optional. */
  secondOfMinute?: number;
  /** Compact = only digit pill + tiny progress; used inside the header. */
  variant?: "compact" | "full";
  className?: string;
  /** Notify parent when phase transitions occur (stoppage/critical/full-time). */
  onStateChange?: (state: MatchClockState) => void;
}

export function MatchClock({
  minute,
  status = "LIVE",
  secondOfMinute,
  variant = "compact",
  className,
  onStateChange,
}: Props) {
  const settings = useMemo(() => getMatchClockSettings(), []);
  const state = useMemo(
    () => deriveMatchClock(minute, status, { secondOfMinute }),
    [minute, status, secondOfMinute],
  );
  // i18n labels in `state` come from localStorage (browser-only). Hide
  // language-dependent text on SSR / first client render to avoid
  // hydration mismatch; reveal after mount.
  const mounted = useMounted();
  const phaseLabel = mounted ? state.phaseLabel : "";
  const remainingLabel = mounted ? state.remainingLabel : "";
  const ariaLabel = mounted
    ? `${state.phaseLabel} ${state.display}`
    : state.display;

  const seenRef = useRef<{
    viewed: boolean;
    critical: boolean;
    stoppage: boolean;
    fullTime: boolean;
  }>({ viewed: false, critical: false, stoppage: false, fullTime: false });

  useEffect(() => {
    if (!settings.enabled) return;
    if (!seenRef.current.viewed) {
      seenRef.current.viewed = true;
      void trackEvent("match_clock_viewed", { variant, phase: state.phase });
    }
    if (state.isCritical && !seenRef.current.critical) {
      seenRef.current.critical = true;
      void trackEvent("critical_time_entered", { phase: state.phase, minute: state.minute });
    }
    if (state.inStoppage && !seenRef.current.stoppage) {
      seenRef.current.stoppage = true;
      void trackEvent("stoppage_time_started", { phase: state.phase, stoppage: state.stoppage });
    }
    if (state.phase === "full_time" && !seenRef.current.fullTime) {
      seenRef.current.fullTime = true;
      void trackEvent("full_time_reached", {});
    }
    onStateChange?.(state);
  }, [state, settings.enabled, variant, onStateChange]);

  if (!settings.enabled) return null;

  const critical = settings.highlightCritical && state.isCritical;

  if (variant === "compact") {
    return (
      <div
        className={`flex flex-col gap-0.5 ${className ?? ""}`}
        aria-label={ariaLabel}
      >
        <div className="flex items-center gap-1.5">
          {critical && <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
          <span
            className="tabular-nums rounded-md border border-border bg-[oklch(0.08_0.005_260)] px-2.5 py-1 text-[14px] font-black leading-none tracking-normal text-[oklch(0.99_0_0)] sm:text-[13px]"
          >
            {state.display}
          </span>
        </div>
        {settings.showProgressBar && (
          <div className="h-[3px] w-full overflow-hidden rounded-full bg-border/60">
            <div
              className={`h-full transition-all ${critical ? "bg-primary" : "bg-foreground/50"}`}
              style={{ width: `${state.progressPct}%` }}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-border bg-[oklch(0.08_0.005_260)] p-3 text-[oklch(0.99_0_0)] shadow-lg ${className ?? ""}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-[oklch(0.99_0_0)]">
          <Timer className="h-3 w-3" />
          {phaseLabel.toUpperCase()}
        </div>
        {critical && (
          <span className="flex items-center gap-1 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold text-primary-foreground">
            <AlertTriangle className="h-2.5 w-2.5" />
            {state.inStoppage ? "+" + state.stoppage : "!"}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <div className="text-2xl font-black tabular-nums tracking-normal text-[oklch(0.99_0_0)]">{state.display}</div>
        <div className="text-[10px] font-semibold text-[oklch(0.9_0_0)]">{phaseLabel}</div>
      </div>
      {settings.showProgressBar && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-border/60">
          <div
            className={`h-full transition-all ${critical ? "bg-primary" : "bg-foreground/40"}`}
            style={{ width: `${state.progressPct}%` }}
          />
        </div>
      )}
      {settings.showRemaining && (
        <div className="mt-1.5 text-[11px] font-semibold text-[oklch(0.9_0_0)]">
          {remainingLabel}
        </div>
      )}
    </div>
  );
}